import { FormModal } from '@/components/form-modal';
import { useToast } from '@/components/ui/hooks/use-toast';
import { createSessions, getSessionsForStudentGroup, getStudentGroups } from '@/lib/api-calls';
import { CreateSessionsForGroupsResults, StudentGroupWithScheduleAndLevel } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';
import { z } from 'zod';

interface GenerateSessionsForOneTimeClassesProps {
  studentGroups: StudentGroupWithScheduleAndLevel[];
  trigger: ReactNode;
  onSuccess?: () => void;
}

const formSchema = z.object({});

export const GenerateSessionsForOneTimeClasses = ({
  studentGroups,
  trigger,
  onSuccess,
}: GenerateSessionsForOneTimeClassesProps) => {
  const studentGroupIds = studentGroups.map((group) => group.id);
  const [results, setResults] = useState<CreateSessionsForGroupsResults>();
  const { mutateAsync } = useMutation({
    mutationFn: createSessions,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const onSubmit = () => {
    return mutateAsync({
      studentGroupIds,
      overrideStartingLessonId: undefined,
      shouldSendEmail: true,
    })
      .then((results) => {
        setResults(results);
        toast({ title: 'Create sessions completed. See result details in the modal' });
        queryClient.invalidateQueries(getStudentGroups());
        for (const groupId of studentGroupIds) {
          queryClient.invalidateQueries(getSessionsForStudentGroup(groupId));
        }
      })
      .catch(() => {
        toast({ title: 'Error while creating sessions' });
      });
  };

  return (
    <FormModal
      modalTitle="Bulk generate sessions"
      formSchema={formSchema}
      trigger={trigger}
      onSubmit={onSubmit}
      submitButtonProps={{
        disabled: !!results,
        style: { display: results ? 'none' : undefined },
      }}
      cancelBtnLabel={results ? 'Close' : 'Cancel'}
      onClose={() => {
        if (results && onSuccess) {
          onSuccess();
        }
        setResults(undefined);
      }}
      defaultValues={{}}
    >
      {() => {
        return (
          <div className="my-6">
            {results ? (
              <div>
                <div>Successfully created sessions for {results.successGroupIds.length} student group(s)</div>
                <div>Found errors while creating sessions for {results.failedGroupIds.length} student group(s) </div>
                {!!results.errors.length && (
                  <div>
                    <ul>
                      {results.errors.map((error) => (
                        <li key={error}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div>New sessions will be generated for {studentGroupIds.length} one-time classes.</div>
            )}
          </div>
        );
      }}
    </FormModal>
  );
};
